import { captureException } from '@sentry/nextjs';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import useSWR, { mutate } from 'swr';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { MOBILE_AUTH_DEEPLINK } from '../lib/mobile/oauth';
import { SentryUser } from '../lib/sentry/sentryUser';

import type { NextPageAuthRequirements } from '../types/page';
import type { MarketingData } from '../utils/cookies';
import { resetStores } from './resetStores';
import { useClientLayoutEffect } from './useClientLayoutEffect';
import type { ApiUserMe } from '@fabric-app/types/user';
import type { OAuthTypes } from '../modules/auth/api/auth.api';
import { authApi } from '../modules/auth/api/auth.api';
import { useAnalyticsStore } from '@fabric-app/business/analytics/analytics.store';
import { AnalyticsEvents } from '@fabric-app/business/analytics/analytics.types';
import { CURRENT_URL } from '../constants/env';
import { warning, removeAlert } from '@fabric-app/business/store/alerts';
import { omit, pick } from '@fabric-app/utils/object/manipulation';
import { useAuthErrorHandler } from '@fabric-app/business/hooks/useAuthErrorHandler';
import { oneOfGroup } from '@fabric-app/business/user/utils/group';
import { appRoutes } from '@fabric-app/business/app/routing/routes.config';
import { ensureError } from '@fabric-app/utils/guards';
import { getErrorStatus } from '@fabric-app/business/errors/errors.utils';
import { getWoodyClient, useWoody } from '@fabric-app/business/woody/woody';
import { useMutationUpdateUserOnboarding } from '@fabric-app/business/user/mutations/useMutationUpdateUserOnboarding';
import { usePostSigninRedirect } from '@fabric-app/business/auth/hooks/usePostSigninRedirect';
import { delayedTrackZustand } from '@fabric-app/compat/capabilities/openreplay';
import { createLogger } from '@fabric-app/utils/logging';
import { defaultQueryClient } from '../lib/react-query/defaultQueryClient';
import { userQueryKeys } from '@fabric-app/business/user/queries/userQueryKeys';
import equal from 'fast-deep-equal';
import { useRunOnce } from '@fabric-app/hooks/useRunOnce';
import { isInMobilePlatform } from '@fabric-app/compat/capabilities/platform';
import { useUserClearCache } from '@/src/modules/user/hooks/useUserClearCache';
const authLogger = createLogger('auth');

type AuthStatus = 'unauthenticated' | 'authenticated' | 'loading';

declare global {
  interface Window {
    loggedIn?: boolean;
    authStatus?: AuthStatus;
  }
}

type Status = {
  loggedIn: boolean;
  authStatus: AuthStatus;
  user: ApiUserMe | null;
};

type AuthEvents = {
  login: ApiUserMe;
  user: ApiUserMe | null;
  logout: undefined;
  status: Status;
};

type EventListener<T> = T extends undefined ? () => void : (arg: T) => void;

interface AuthStore {
  authStatus: AuthStatus;
  user: ApiUserMe | null;
  isLoggedIn: boolean;
  isRedirecting: boolean;

  listeners: {
    [K in keyof AuthEvents]?: EventListener<AuthEvents[K]>[];
  };

  on: <K extends keyof AuthEvents>(event: K, listener: EventListener<AuthEvents[K]>) => () => void;
  emit: <K extends keyof AuthEvents>(event: K, arg: AuthEvents[K]) => void;

  login: (email: string, password: string) => Promise<ApiUserMe>;
  handleOAuthCallback: (options: {
    provider: OAuthTypes;
    code: string;
    mobile?: boolean;
    marketing?: MarketingData | null;
  }) => Promise<ApiUserMe>;
  logout: () => Promise<void>;

  // Modifying state functions
  setAuthStatus: (status: AuthStatus) => void;
  setUser: (user: ApiUserMe) => void;
  setIsLoggedIn: (isLoggedIn: boolean, status: AuthStatus) => void;
  setIsRedirecting: (isRedirecting: boolean) => void;
  getUser: () => Promise<ApiUserMe>;
  signUpEmail: string | null;
  setSignUpEmail: (email: string | null) => void;

  /**
   * Forced optimistic subscription state to allow Woody, Stripe and RevenueCat information to settle
   * after a successful subscription purchase or alteration.
   * Since this works with server-to-server webhooks sometimes there is some desync between the client and the server which cause issues.
   */
  forcedOptimisticSubscription: ApiUserMe['subscription'] | null;
  forcedOptimisticSubscriptionTimeout: number | null;
  setForcedOptimisticSubscription: (
    state: ApiUserMe['subscription'] | null,
    timeout?: number,
  ) => void;

  /**
   * @deprecated use usePostSigninRedirect instead
   */
  redirectUrlAfterAuth: string | null;
  setRedirectUrlAfterAuth: (url: string | null) => void;
}

const clearCache = () => mutate(() => true, undefined, { revalidate: false });

export const getInitialAuthScreen = () =>
  isInMobilePlatform() ? appRoutes.auth.signup : appRoutes.auth.signin;

const useAuthStore = create<AuthStore>()(
  delayedTrackZustand(
    persist(
      (set, get) => ({
        redirectUrlAfterAuth: null,
        setRedirectUrlAfterAuth: (url) => set({ redirectUrlAfterAuth: url }),
        authStatus: 'loading' as const,
        signUpEmail: null,
        setSignUpEmail: (email) => set({ signUpEmail: email }),
        user: null,
        roots: [],
        isLoggedIn: false,
        isRedirecting: false,

        forcedOptimisticSubscription: null,
        forcedOptimisticSubscriptionTimeout: null,
        setForcedOptimisticSubscription: (state, timeout = 5000) => {
          if (get().forcedOptimisticSubscriptionTimeout) {
            clearTimeout(get().forcedOptimisticSubscriptionTimeout ?? 0);
          }

          set({
            forcedOptimisticSubscription: state,
            forcedOptimisticSubscriptionTimeout: window.setTimeout(() => {
              mutate('woody-user').finally(() => {
                set({
                  forcedOptimisticSubscription: null,
                  forcedOptimisticSubscriptionTimeout: null,
                });
              });
            }, timeout),
          });
        },

        listeners: {},

        on: (event, listener) => {
          set((state) => {
            const listenersForEvent = state.listeners[event] || [];
            return {
              ...state,
              listeners: {
                ...state.listeners,
                [event]: [...listenersForEvent, listener],
              },
            };
          });

          // Return the unsubscribe function
          return () => {
            set((state) => {
              const updatedListeners = state.listeners[event]?.filter((l) => l !== listener) || [];
              return {
                ...state,
                listeners: {
                  ...state.listeners,
                  [event]: updatedListeners,
                },
              };
            });
          };
        },

        emit: (event, arg) => {
          const state = get();
          const listenersForEvent = state.listeners[event];
          if (listenersForEvent) {
            for (const listener of listenersForEvent) {
              listener(arg as never);
            }
          }
        },
        getUser: async () => {
          const client = await getWoodyClient();

          const userResponse = await client.v2('/v2/user/me');

          /**
           * set the react query data
           */
          defaultQueryClient.setQueryData<ApiUserMe>(userQueryKeys.userInfo(), (prevData) => {
            return equal(prevData, userResponse) ? prevData : userResponse;
          });

          try {
            SentryUser.set(userResponse);

            set({
              authStatus: 'authenticated',
              isLoggedIn: true,
              user: userResponse,
              isRedirecting: false,
            });

            get().emit('status', {
              authStatus: 'authenticated',
              user: userResponse,
              loggedIn: true,
            });

            get().emit('user', userResponse);

            await mutate('woody-roots');
            await mutate('woody-user', userResponse, false);

            const { identify, track } = useAnalyticsStore.getState();
            identify(userResponse);
            track(AnalyticsEvents.EmailLogin, { userId: userResponse.id });

            return userResponse;
          } catch (e) {
            SentryUser.clear();
            throw ensureError(e);
          }
        },
        login: async (email, password) => {
          const client = await getWoodyClient();

          await client.v2('/v2/authentication/login', {
            method: 'post',
            headers: {
              'x-csrf-token': await authApi.getCSFRToken(),
            },
            body: {
              email,
              password,
            },
          });

          await resetStores();
          await clearCache();

          set({
            isLoggedIn: true,
            authStatus: 'authenticated',
            isRedirecting: false,
          });

          const userResponse = await get().getUser();
          get().emit('login', userResponse);
          return userResponse;
        },
        handleOAuthCallback: async ({ provider, code, mobile, marketing }) => {
          const client = await getWoodyClient();

          const callbackUri = mobile ? `${MOBILE_AUTH_DEEPLINK}://oauth` : `${CURRENT_URL}/oauth`;

          const { operation } = await client.v2(
            {
              endpoint: '/v2/authentication/oauth/{provider}/callback',
              params: {
                provider,
              },
            },
            {
              method: 'post',
              body: {
                code,
                redirectUri: callbackUri,
                marketing,
              },
            },
          );

          await resetStores();
          await clearCache();

          set({
            isLoggedIn: true,
            authStatus: 'authenticated',
            isRedirecting: false,
          });

          const userResponse = await get().getUser();

          if (operation === 'signup') {
            const { identify } = useAnalyticsStore.getState();
            identify(userResponse);
          }

          get().emit('login', userResponse);
          return userResponse;
        },

        logout: async () => {
          authLogger.debug('logout');

          const client = await getWoodyClient();
          const user = get().user;
          set({
            authStatus: 'unauthenticated',
            user: null,
            isLoggedIn: false,
          });

          get().emit('logout', undefined);

          try {
            await client.v2('/v2/authentication/logout', {
              method: 'delete',
            });
          } catch (e) {
            if (getErrorStatus(e) === 401) {
              // user is already logged out so BE will return 401, it's fine
            } else {
              captureException(ensureError(e));
            }
          }

          const { identify, track } = useAnalyticsStore.getState();
          identify(user);
          track(AnalyticsEvents.Logout, { userId: user?.id });
          SentryUser.clear();
        },

        setAuthStatus: (status) => {
          set({ authStatus: status });
          get().emit('status', {
            authStatus: status,
            user: get().user,
            loggedIn: get().isLoggedIn,
          });
        },
        setUser: (user) => {
          set({ user, isLoggedIn: true, authStatus: 'authenticated', isRedirecting: false });
          get().emit('user', user);
        },
        setIsLoggedIn: (isLoggedIn, authStatus) => {
          set({
            isLoggedIn,
            authStatus,
          });
          get().emit('status', {
            authStatus: get().authStatus,
            user: get().user,
            loggedIn: isLoggedIn,
          });
        },
        setIsRedirecting: (isRedirecting) => set({ isRedirecting }),
      }),
      {
        name: 'fabric-auth-storage',
        version: 2.1,

        partialize: (state) => {
          // Access token is not stored in localStorage for security reasons
          // Auth status is not stored to make sure we always get the latest status
          const storeable = omit(state, ['authStatus', 'isRedirecting', 'listeners', 'isLoggedIn']);

          return {
            authStatus: 'loading',
            ...storeable,
          };
        },
      },
    ),
    'auth',
  ),
);

export const useAuthInitializer = (authRequirements: NextPageAuthRequirements) => {
  const router = useRouter();
  const { client } = useWoody();

  /**
   * should probably use the useUserLogout hook instead of authStore.logout and useUserClearCache
   * but that'd be circular dependency between two files, which can cause issues
   *
   * reworking incoming, not doing pretty rn
   */
  const clearUserCache = useUserClearCache();
  const {
    user: storedUser,
    setUser,
    setIsLoggedIn,
    authStatus,
    isLoggedIn,
    isRedirecting,
    setIsRedirecting,
    logout,
  } = useAuthStore(
    (state) =>
      pick(state, [
        'user',
        'setUser',
        'setIsLoggedIn',
        'isLoggedIn',
        'isRedirecting',
        'setIsRedirecting',
        'authStatus',
        'logout',
      ]),
    shallow,
  );

  const [offline, setOffline] = useState(false);
  useEffect(() => {
    const handleOffline = () => setOffline(true);
    const handleOnline = () => setOffline(false);

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  useEffect(() => {
    if (!offline) return;

    const alert = warning({
      content: (
        <p>
          You are currently offline.{' '}
          <a
            href="#"
            style={{
              color: 'var(--color-primary)',
              textDecoration: 'underline',
            }}
            onClick={() => window.location.reload()}
          >
            Click here to refresh.
          </a>
        </p>
      ),
      disableTimeout: true,
    });

    return () => {
      alert && removeAlert(alert.id);
    };
  }, [offline]);

  const [retrying, setRetrying] = useState(true);
  const { data: user, isLoading } = useSWR(
    authStatus !== 'unauthenticated' || retrying ? 'woody-user' : null,
    async () => {
      if (authStatus !== 'authenticated') {
        setIsLoggedIn(false, 'loading');
      }

      const res = client.v2('/v2/user/me');
      return res;
    },
    {
      // Since most data is cached it should be fine to be a bit higher than 100ms
      dedupingInterval: 400,
      // Make sure we only re-check at a good interval, since this will do a backend request
      refreshInterval: 1000 * 60 * 5, // 5 minutes
      focusThrottleInterval: 1000 * 60 * 2, // 2 minutes,
      keepPreviousData: true,
      fallbackData: storedUser ?? undefined,
      errorRetryCount: 6,
      errorRetryInterval: 1000,
      shouldRetryOnError: true,
      revalidateOnReconnect: true,
      onSuccess: (data) => {
        SentryUser.set(data);
        if (data) {
          /**
           * set user automatically updates auth status and other properties
           */
          setUser(data);
          setRetrying(false);
        }
      },
      onErrorRetry: async (error, _key, _config, revalidate, { retryCount }) => {
        SentryUser.clear();
        setRetrying(true);

        const alwaysRetry =
          window.location.pathname === '/share-intent' || window.fabricMobile?.isSheet;

        // If no user stored and we get a 401 we assume the user is logged out and make sure the log out is recognized
        if ((error.status === 401 && !alwaysRetry) || retryCount > 5) {
          setIsLoggedIn(false, 'unauthenticated');
          setRetrying(false);

          if (authRequirements.requiresAuth) {
            setIsRedirecting(true);
            setIsLoggingOut(true);
            clearUserCache();
            await logout(); // logout will clear cookie
            router.push(getInitialAuthScreen());
          }
          return;
        }

        setRetrying(true);
        setTimeout(
          () => revalidate({ retryCount, dedupe: true }),
          retryCount > 2 ? (retryCount - 2) * 1000 * 60 : retryCount * 1000,
        );
      },
    },
  );

  useEffect(() => {
    window.loggedIn = isLoggedIn;
    window.authStatus = authStatus;
  }, [isLoggedIn, authStatus]);

  const authStatusRef = useRef(authStatus);
  useEffect(() => {
    authStatusRef.current = authStatus;
  }, [authStatus]);

  const [isLoggingOut, setIsLoggingOut] = useState(false);
  useClientLayoutEffect(() => {
    if (authStatus !== 'unauthenticated' && user && isLoggedIn) {
      if (isLoggingOut) {
        setIsRedirecting(false);
        setIsLoggingOut(false);
      }
      return;
    }

    if (
      !authRequirements.requiresAuth ||
      isRedirecting ||
      isLoading ||
      authStatus === 'loading' ||
      retrying ||
      offline
    )
      return;

    const initialAuthScreen = getInitialAuthScreen();
    if (router.pathname === initialAuthScreen) return;

    authLogger.debug('logout', {
      authStatus,
      isLoading,
      isLoggedIn,
      isLoggingOut,
      isRedirecting,
      authRequirements,
      retrying,
      offline,
    });
    clearUserCache();
    logout();

    router.push(initialAuthScreen);
    setIsRedirecting(true);
    setIsLoggingOut(true);
  }, [
    authStatus,
    isLoading,
    isLoggedIn,
    isLoggingOut,
    isRedirecting,
    setIsRedirecting,
    logout,
    offline,
    authRequirements.requiresAuth,
    retrying,
    router,
    user,
  ]);

  useClientLayoutEffect(() => {
    if (
      isRedirecting ||
      !user ||
      !authRequirements.requiredGroups ||
      (authRequirements.requiredGroups && oneOfGroup(user, authRequirements.requiredGroups))
    )
      return;

    setIsRedirecting(true);
    router.replace('/404').then(() => {
      setIsRedirecting(false);
    });
  }, [setIsRedirecting, user, authRequirements.requiredGroups, authStatus, isRedirecting, router]);

  useAuthErrorHandler(client, logout);
};

export const useAuthUser = () =>
  useAuthStore(
    (state) =>
      state.user
        ? {
            ...state.user,
            subscription: state.forcedOptimisticSubscription ?? state.user?.subscription,
          }
        : null,
    shallow,
  );
export const useAuthIsLoggedIn = () => useAuthStore((state) => state.isLoggedIn, shallow);
export const useAuthStatus = () => {
  const { authStatus } = useAuthStore((state) => pick(state, ['authStatus']), shallow);

  return useMemo(() => authStatus, [authStatus]);
};

export const forceUserRefresh = async () => mutate('woody-user');

const logger = createLogger('useRedirectAfterSuccessAuth');

export const useRedirectAfterSuccessAuth = () => {
  const router = useRouter();
  const redirectUrlAfterAuth =
    useAuthStore((state) => state.redirectUrlAfterAuth) || appRoutes.home;

  const { postSigninRedirect, clearPostSigninRedirect } = usePostSigninRedirect();

  return async (
    user: {
      onboarding: { isFrontendTutorialCompleted?: boolean | null | undefined };
    },
    options: {
      action: 'login' | 'confirm' | 'oauth' | 'socket-event';
      forcedOnboarding?: boolean;
      /**
       * will be passed to the captureException
       */
      extra?: Record<string, unknown>;
    },
  ) => {
    try {
      if (!user.onboarding?.isFrontendTutorialCompleted) {
        logger.debug('show tutorial', user.onboarding);

        // Redirect next tick to ensure the request to update the user onboarding status is sent
        setTimeout(() => {
          router.push(appRoutes.onboarding.profile);
        }, 0);
      } else {
        clearPostSigninRedirect();

        const nextUrl = options.forcedOnboarding
          ? appRoutes.onboarding.profile
          : (postSigninRedirect?.url ?? redirectUrlAfterAuth);

        logger.debug('no tutorial', user.onboarding, '; next url: ', nextUrl);

        router.push(nextUrl);
      }
    } catch (e) {
      /**
       * Just a fail save if for some reason the redirects KO
       * this shouldn't happen but just to be sure
       */
      captureException(new Error('Failed to redirect after' + options.action), {
        extra: {
          error: e,
          action: options.action,
          ...options.extra,
        },
      });

      clearPostSigninRedirect();
      router.push(postSigninRedirect?.url ?? redirectUrlAfterAuth);
    }
  };
};

export const useUpdateOnboardingStatus = () => {
  const user = useAuthUser();
  const { mutate } = useMutationUpdateUserOnboarding();
  useRunOnce(
    () => {
      const referrerUrl = document.referrer;
      const isFromTutorialPage = referrerUrl.includes(appRoutes.onboarding.connectingTheDots);
      if (!isFromTutorialPage) return;

      setTimeout(() => {
        mutate({
          body: {
            isFrontendTutorialCompleted: true,
          },
        });
      }, 0);
    },
    {
      condition: !!user,
      useEffectFn: useClientLayoutEffect,
    },
  );
};

export default useAuthStore;
